import { useState } from 'react';
import { siteInfo, reputationItems, faqItems, contactInfo } from '@/mocks/site';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotField = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);
    const submitUrl = 'https://readdy.ai/api/form/d9q1j5knlsngrm4llq70';

    try {
      const response = await fetch(submitUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse errors
      }

      if (response.ok && parsed.code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后再试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 暗网51 | 品牌介绍与联系方式"
        description="了解暗网51的品牌故事、团队背景和使命愿景。暗网51是专注于暗网生态深度解析的权威资讯平台，致力于为用户提供最专业的网络安全知识和防护指南。"
        keywords="暗网51,关于我们,品牌介绍,联系方式,网络安全平台"
        canonicalUrl="https://www.darkweb51.com/about"
      />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-50 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        <section className="bg-background-100 py-12 md:py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6 text-center">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 mb-4">
              关于{siteInfo.name}
            </h1>
            <p className="text-sm md:text-lg text-foreground-500 max-w-3xl mx-auto leading-relaxed">
              {siteInfo.description}
            </p>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                我们的使命
              </h2>
              <p className="text-base text-foreground-700 leading-relaxed mb-4">
                成立于{siteInfo.founded}年，暗网51致力于成为中国领先的网络安全知识普及平台。在数字化浪潮席卷全球的今天，网络安全已经不再是少数技术专家的专属领域，而是每个人都应该了解和掌握的基本技能。
              </p>
              <p className="text-base text-foreground-700 leading-relaxed mb-4">
                我们的团队由资深安全研究人员、网络威胁情报分析师和专业内容创作者组成。我们持续追踪暗网生态的最新动态，将复杂的安全概念转化为通俗易懂的知识内容，帮助广大用户建立起坚实的网络安全防线。
              </p>
              <p className="text-base text-foreground-700 leading-relaxed">
                我们坚信：知识是最好的防火墙。通过普及暗网安全知识，我们希望能够减少网络犯罪对普通用户的伤害，提升全社会的网络安全意识。
              </p>
            </div>
            <div className="aspect-[4/3] rounded-lg overflow-hidden bg-background-100">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20digital%20security%20team%20working%20on%20cybersecurity%2C%20warm%20golden%20and%20teal%20accents%2C%20minimalist%20professional%20office%20environment%2C%20clean%20modern%20aesthetic%2C%20collaborative%20workspace%20atmosphere%2C%20high%20quality%20editorial%20photography&width=800&height=600&seq=about-mission-01&orientation=landscape"
                alt="暗网51团队"
                title="暗网51团队"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-12 md:py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                行业口碑
              </h2>
              <p className="text-sm text-foreground-500">来自合作伙伴与用户的真实评价</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
              {reputationItems.map((item) => (
                <div
                  key={item.id}
                  className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <i className="ri-double-quotes-l text-accent-500 text-xl"></i>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    {item.content}
                  </p>
                  <div className="border-t border-background-200 pt-3">
                    <p className="text-sm font-semibold text-foreground-900">{item.name}</p>
                    <p className="text-xs text-foreground-500">{item.title}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              常见问题
            </h2>
            <p className="text-sm text-foreground-500">关于暗网51的常见疑问解答</p>
          </div>
          <div className="space-y-3 max-w-3xl mx-auto">
            {faqItems.map((item, index) => (
              <details
                key={index}
                className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden"
              >
                <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer text-base font-medium text-foreground-900 hover:text-primary-600 transition-colors">
                  {item.question}
                  <i className="ri-arrow-down-s-line text-lg transition-transform group-open:rotate-180"></i>
                </summary>
                <div className="px-4 md:px-5 pb-4 md:pb-5">
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </section>

        <section className="bg-background-100 py-12 md:py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500">如有任何问题或合作意向，欢迎与我们取得联系</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg shrink-0">
                    <i className="ri-mail-line text-accent-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                    <p className="text-sm text-foreground-600">{contactInfo.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg shrink-0">
                    <i className="ri-wechat-line text-accent-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">微信公众号</p>
                    <p className="text-sm text-foreground-600">{contactInfo.wechat}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg shrink-0">
                    <i className="ri-phone-line text-accent-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">联系电话</p>
                    <p className="text-sm text-foreground-600">{contactInfo.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg shrink-0">
                    <i className="ri-map-pin-line text-accent-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">办公地址</p>
                    <p className="text-sm text-foreground-600">{contactInfo.address}</p>
                    <p className="text-xs text-foreground-400 mt-1">{contactInfo.workingHours}</p>
                  </div>
                </div>
              </div>

              <div>
                <form
                  onSubmit={handleSubmit}
                  className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 space-y-4"
                  data-readdy-form="contact"
                  action="https://readdy.ai/api/form/d9q1j5knlsngrm4llq70"
                  method="POST"
                >
                  <input
                    type="text"
                    name="website_alt"
                    className="honeypot-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      姓名 <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      placeholder="请输入您的姓名"
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-md text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      邮箱 <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      placeholder="请输入您的邮箱地址"
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-md text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      主题
                    </label>
                    <input
                      id="contact-subject"
                      name="subject"
                      type="text"
                      placeholder="请简要描述您的需求"
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-md text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      留言 <span className="text-accent-600">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      placeholder="请输入您的留言内容（500字以内）"
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-md text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="w-full px-6 py-3 bg-primary-500 text-white rounded-md font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    发送留言
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-primary-600 text-center">感谢您的留言，我们会尽快回复！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-accent-600 text-center">{formError || '提交失败，请稍后再试'}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '暗网51',
            url: 'https://www.darkweb51.com/',
            description: siteInfo.description,
            email: contactInfo.email,
            telephone: contactInfo.phone,
            address: {
              '@type': 'PostalAddress',
              addressLocality: '北京',
              addressCountry: 'CN',
              streetAddress: '海淀区中关村科技园区',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqItems.map((item) => ({
              '@type': 'Question',
              name: item.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.answer,
              },
            })),
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://www.darkweb51.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '关于我们',
              },
            ],
          }),
        }}
      />
    </>
  );
}